import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { SystemNotificationLinkType, SystemNotification } from './SystemNotification/SystemNotification';
import { SystemNotificationContent } from './SystemNotification/SystemNotificationContent';
import { createSystemNotificationAcknowledge, getUnreadSystemNotifications } from '../../services';
import { formatNotificationMessageDates, getLanguageSpecificMessage } from '../../helpers';
import { ISystemNotificationItem } from '../../interfaces';

interface ISystemNotificationListProps {
    userId: string;
    currentLanguageId: number;
    language: string;
}

export const SystemNotificationList = ({
    language,
    currentLanguageId,
    userId,
}: ISystemNotificationListProps): JSX.Element => {
    const { formatMessage } = useIntl();
    const [notifications, setNotifications] = useState<ISystemNotificationItem[]>([]);

    useEffect(() => {
        getNotifications();
    }, []);

    const getNotifications = async (): Promise<void> => {
        try {
            const { items } = await getUnreadSystemNotifications();
            setNotifications(items);
        } catch (error) {
            console.error(error);
        }
    };

    const handleRemoveClick = async (systemNotificationId: number): Promise<void> => {
        try {
            await createSystemNotificationAcknowledge(systemNotificationId, userId);
            await getNotifications();
        } catch (error) {
            console.error(error);
        }
    };

    const systemNotificationElements: JSX.Element[] = notifications
        .map((notification) => {
            // The notifications are hard coupled and not really extendable.
            // this is just a hotfix and a failsafe for ensuring the link only appears for the notification with id 1
            const shouldHaveProfileLink = notification.id === 1;
            const message = getLanguageSpecificMessage(notification, currentLanguageId);

            return !message ? null : (
                <SystemNotification
                    key={'SystemNotification' + notification.id}
                    notificationId={notification.id}
                    linkTypes={[SystemNotificationLinkType.REMOVE]}
                    onRemoveClick={handleRemoveClick}
                >
                    <SystemNotificationContent>
                        {formatNotificationMessageDates(message, language)}
                        {shouldHaveProfileLink && (
                            <a className="d-flex align-items-center p-0" href="/settings/profile">
                                {formatMessage({ id: 'TEXT_SETTINGS_PROFILE' })}
                            </a>
                        )}
                    </SystemNotificationContent>
                </SystemNotification>
            );
        })
        .filter(Boolean);

    return systemNotificationElements.length < 1 ? null : (
        <div className="d-flex flex-column mb-4 mt-3">{systemNotificationElements}</div>
    );
};
